export type TimeUnit = 'day' | 'hour' | 'minute' | 'second';

export interface TimeSinceOptions {
  /**
   * How many units to display (e.g. `2` → "1 day 3 hours"). Defaults to a
   * single unit for minutes/seconds, but pairs the leading day/hour with the
   * next non-zero unit (e.g. "1 day 3 hours ago", "1 hour 32 minutes ago", or
   * "1 day 15 minutes ago" when the intermediate unit is zero).
   */
  maxUnits?: number;
  /** Smallest unit to include. Defaults to `'second'`. */
  smallestUnit?: TimeUnit;
  /** Largest unit to include. Defaults to `'day'`. */
  largestUnit?: TimeUnit;
  /** Append " ago". Defaults to `true`. */
  suffix?: boolean;
}

export type TimeUntilOptions = Omit<TimeSinceOptions, 'suffix'> & {
  /** Prepend "in ". Defaults to `true`. */
  prefix?: boolean;
};

const UNIT_SECONDS: Record<TimeUnit, number> = {
  day: 86400,
  hour: 3600,
  minute: 60,
  second: 1,
};

// Ordered from largest to smallest.
const UNIT_ORDER: TimeUnit[] = ['day', 'hour', 'minute', 'second'];

const pluralize = (value: number, unit: TimeUnit): string =>
  `${value} ${unit}${value === 1 ? '' : 's'}`;

function formatUnitBreakdown(
  secondsElapsed: number,
  {
    maxUnits,
    smallestUnit = 'second',
    largestUnit = 'day',
  }: Pick<TimeSinceOptions, 'maxUnits' | 'smallestUnit' | 'largestUnit'>,
): string {
  // Normalize the range so an inverted largest/smallest never yields an empty
  // slice (which would later format NaN).
  const largestIndex = UNIT_ORDER.indexOf(largestUnit);
  const smallestIndex = UNIT_ORDER.indexOf(smallestUnit);
  const units = UNIT_ORDER.slice(
    Math.min(largestIndex, smallestIndex),
    Math.max(largestIndex, smallestIndex) + 1,
  );

  // Most-significant unit that has a whole value (>= 1). Sub-unit dates fall
  // back to the smallest unit so we still render something.
  const leadingIndex = units.findIndex(
    (unit) => secondsElapsed >= UNIT_SECONDS[unit],
  );
  const startIndex = leadingIndex === -1 ? units.length - 1 : leadingIndex;
  const leadingUnit = units[startIndex];

  // Default: single unit, but pair days with hours and hours with minutes.
  const unitsToShow =
    maxUnits ?? (leadingUnit === 'day' || leadingUnit === 'hour' ? 2 : 1);

  const parts: string[] = [];
  let remainder = secondsElapsed;
  for (
    let i = startIndex;
    i < units.length && parts.length < unitsToShow;
    i++
  ) {
    const unit = units[i];
    const value = Math.floor(remainder / UNIT_SECONDS[unit]);
    remainder -= value * UNIT_SECONDS[unit];
    // Always show the leading unit; skip zero-valued secondary units.
    if (value === 0 && parts.length > 0) continue;
    parts.push(pluralize(value, unit));
  }

  return parts.join(' ');
}

export function timeSince(date: Date, options: TimeSinceOptions = {}): string {
  const { suffix = true, ...breakdownOptions } = options;
  const secondsPast = (Date.now() - date.getTime()) / 1000;
  const text = formatUnitBreakdown(secondsPast, breakdownOptions);
  return suffix ? `${text} ago` : text;
}

/**
 * Formats the time remaining until a future date, e.g. "in 1 day 9 hours".
 * Mirrors `timeSince`, but counts down to the date instead of counting up
 * from it. Past dates return the breakdown without a leading "in ".
 */
export function timeUntil(date: Date, options: TimeUntilOptions = {}): string {
  const { prefix = true, ...breakdownOptions } = options;
  const secondsUntil = (date.getTime() - Date.now()) / 1000;
  const text = formatUnitBreakdown(secondsUntil, breakdownOptions);
  return prefix && secondsUntil > 0 ? `in ${text}` : text;
}

export function formatMinutesHumanReadable(totalMinutes: number): string {
  if (totalMinutes < 60) {
    return `${totalMinutes} minute${totalMinutes === 1 ? '' : 's'}`;
  }
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  return parts.join(' ');
}
